import { useContext, useEffect, useState } from 'react';
import { Button, TextField, Typography } from 'darrius';
import { axiosInstance } from '../../../../utils/axiosInstance';
import { CheckoutContext } from '../../../../context/Checkout';
import { showToast } from '../../../atoms/Toast/Toast';
import { customTracking } from '../../../../utils/customTracking';
import styles from './PixView.module.scss';
import { addCpfOrCnpjMask } from '../../../../utils/parsedMasks';

const CPF_REGEX_VALIDATOR = /^\d{3}\.\d{3}\.\d{3}\-\d{2}$/;
const CNPJ_REGEX_VALIDATOR = /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/;

export const PixView = ({ appButton, origin, isMercadoPago, userState }) => {
  const { checkoutData, updateCheckoutData } = useContext(CheckoutContext);
  const [loading, setLoading] = useState(false);
  const [pixCode, setPixCode] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const [showCpfPayer, setShowCpfPayer] = useState(true);
  const [disabledButton, setDisabledButton] = useState(true);
  const [cpfPayer, setCpfPayer] = useState('');
  const [checked, setChecked] = useState(false);
  const [cpfMessage, setCpfMessage] = useState('');

  const activeBundle = checkoutData.bundlesData.find(
    (bundle) => bundle.nameId === checkoutData.currentBundleName
  );

  function handleChange(cpf: string) {
    setCpfPayer(cpf);
    if (CPF_REGEX_VALIDATOR.test(cpf) || CNPJ_REGEX_VALIDATOR.test(cpf)) {
      setDisabledButton(false);
      setCpfMessage('');
    } else {
      setDisabledButton(true);
      setCpfMessage('Digite um CPF/CNPJ válido!');
    }
  }

  async function handleGeneratePixCode() {
    setLoading(true);
    try {
      const requestOptionsDefault = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          paymentMethod: 'pix',
          bundleOption: activeBundle.purchaseLink,
          coupon: showCoupon ? checkoutData.coupon : '',
          cpf: checked ? null : cpfPayer,
          origin,
          userState,
        },
      };

      const mercadoPagoRequestOptions = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/pix_mercado_pago`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          bundleOption: activeBundle.purchaseLink,
          cpf: checked ? null : cpfPayer,
          activeBundle,
        },
      };

      const requestOptions = isMercadoPago
        ? mercadoPagoRequestOptions
        : requestOptionsDefault;

      const { data } = await axiosInstance(requestOptions);

      const pixCode = isMercadoPago ? data.ticket_url : data.barcode;

      setPixCode(pixCode);
      setLoading(false);
      if (window?.newrelic) {
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'pix_generate_code_success',
          origin: origin,
          bundle_id: activeBundle?.id,
          invoice_id: data.invoice_id,
        });
      }
      customTracking({
        schema: 'iglu:br.com.getninjas/pix_payment/jsonschema/1-0-0',
        data: {
          profile_id: checkoutData.profile.id,
          bundle_id: activeBundle.id,
          action: 'click',
          invoice_id: data.invoice_id,
          step: 'pix_payment',
          label: 'copy_code_click_success',
        },
      });
    } catch (error) {
      setLoading(false);
      if (window?.newrelic) {
        window.newrelic.noticeError(error, {
          step: 'pix_generate_code_error',
          bundle_id: activeBundle?.id,
          status: error?.response?.status,
          origin: origin,
          message: error?.response?.data?.clientMessage || error?.message,
        });
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'pix_generate_code_error',
          bundle_id: activeBundle?.id,
          status: error?.response?.status,
          message: error?.response?.data?.clientMessage || error?.message,
        });
      }
      showToast({
        message: error.response.data.clientMessage,
        type: 'error',
      });
    }
  }

  function handleCopyPixCode() {
    navigator.clipboard.writeText(pixCode);
    showToast({ message: 'Código copiado com sucesso!', type: 'success' });
  }

  async function handleApplyDiscounts() {
    try {
      const { data } = await axiosInstance({
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/discounts`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          bundle: activeBundle.purchaseLink,
          coupon: checkoutData.coupon,
          origin,
          user_state: userState,
        },
      });

      const bundleWithDiscounts = {
        ...activeBundle,
        totalPrice: data.current_value,
        installmentValue: data.current_installments_details.max_part_price,
        installmentsLabel: data.current_value_installments,
        installments: data.installments,
      };

      updateCheckoutData((oldState) => {
        const bundles = oldState.bundlesData.filter(
          (bundle) => bundle.nameId !== checkoutData.currentBundleName
        );

        return {
          ...oldState,
          bundlesData: [...bundles, bundleWithDiscounts].sort(
            (a, b) => a.id - b.id
          ),
        };
      });
      showToast({
        type: 'success',
        message: 'Cupom aplicado',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message:
          error.response.data.clientMessage ||
          'Não foi possivel aplicar o cupom',
      });
    }
  }

  useEffect(() => {
    customTracking({
      schema: 'iglu:br.com.getninjas/payment_tab_click/jsonschema/1-0-0',
      data: {
        payment_type: 'pix',
        profile_id: checkoutData.profile.id,
        bundle_id: activeBundle.id,
      },
    });
  }, []);

  return showCpfPayer ? (
    <div className={`pix-view ${styles['pix-view']} `}>
      <div
        className={`recurrency-modal__image ${styles['recurrency-modal__image']} `}
      >
        <img
          src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/seguranca12.svg`}
          alt="Ilustração de segurança"
        />
      </div>

      <div>
        <Typography.Subtitle size="m">
          Para continuar, insira o CPF ou CNPJ do pagador
        </Typography.Subtitle>
        <Typography.Text className={styles.text}>
          Isso garantirá ainda mais segurança no seu pagamento.
        </Typography.Text>
      </div>
      <div className={`pix-view ${styles['pix__label']} `}>
        <TextField
          id="cpf"
          label="CPF/CNPJ"
          disabled={checked}
          placeholder="Digite aqui o CPF/CNPJ"
          maxLength={18}
          value={checked ? '' : addCpfOrCnpjMask(cpfPayer) || ''}
          onChange={(evt) => handleChange(evt.target.value)}
          errorMessage={checked ? '' : cpfMessage}
        />
      </div>
      <div className={`pix-coupon ${styles['pix-coupon']} `}>
        <input
          type="checkbox"
          id="user_cpf"
          onChange={() => {
            setChecked(!checked);

            if (checked) {
              setCpfMessage('');
              setCpfPayer('');
              setDisabledButton(true);
            } else {
              setDisabledButton(false);
            }
          }}
        />
        <label htmlFor="coupon">Utilizar CPF/CNPJ cadastrado</label>
      </div>

      <Button.Root
        variation="primary"
        type="button"
        fullWidth
        size="small"
        disabled={disabledButton}
        isLoading={loading}
        onClick={() => {
          setShowCpfPayer(false), checked && setCpfPayer('');
        }}
      >
        Continuar
      </Button.Root>
    </div>
  ) : (
    <div className={`pix-view ${styles['pix-view']} `}>
      <div className="pix-img__container">
        <img
          src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/pix-logo.png`}
          alt="Imagem representativa do logo do pix"
        />
      </div>

      <div>
        <Typography.Subtitle size="m">
          Pague com o código do PIX
        </Typography.Subtitle>
        <Typography.Text className={styles.text}>
          Copie o código gerado e pague pelo app do seu banco na opção PIX.
        </Typography.Text>
      </div>

      <div className={`pix-coupon ${styles['pix-coupon']} `}>
        <input
          type="checkbox"
          id="coupon"
          onChange={() => setShowCoupon(!showCoupon)}
        />
        <label htmlFor="coupon">Usar cupom</label>
      </div>

      {showCoupon && (
        <div className="coupon__container">
          <TextField
            id="pix-coupon"
            className="coupon__input"
            label="Cupom"
            placeholder="Cupom"
            value={checkoutData.coupon}
            onChange={(evt) =>
              updateCheckoutData((oldState) => ({
                ...oldState,
                coupon: evt.target.value,
              }))
            }
          />

          <Button.Root
            size="small"
            variation="secondary"
            onClick={handleApplyDiscounts}
          >
            Aplicar
          </Button.Root>
        </div>
      )}

      {pixCode ? (
        <>
          <textarea
            className={`pix-code ${styles['pix-code']} `}
            disabled
            defaultValue={pixCode}
          />
          <Button.Root
            variation="secondary"
            type="button"
            fullWidth
            size="small"
            onClick={handleCopyPixCode}
          >
            Copiar código PIX
          </Button.Root>
        </>
      ) : (
        <Button.Root
          variation="primary"
          type="button"
          fullWidth
          size="small"
          disabled={loading}
          isLoading={loading}
          onClick={handleGeneratePixCode}
        >
          Gerar código PIX
        </Button.Root>
      )}
    </div>
  );
};
